import { useEffect, useState, useContext } from 'react';
import { toast } from 'react-toastify';
import { Navigate, useNavigate } from 'react-router-dom';
import useApi from '../../../hooks/useApi';
import Modal from '../../../components/Modal';
import FormInput from '../../../components/FormInput';
import apiService from '../../../services/apiService';
import { FaEdit, FaTrash, FaCheck, FaTimes, FaDownload, FaSearch } from 'react-icons/fa';
import { AuthContext } from '../../../context/AuthContext';

function MyWasteBids() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useContext(AuthContext);
  const { data: bidsData, loading: bidsLoading, error: bidsError, fetchData: fetchBids } = useApi(`/waste/my-bids/${user?.id || ''}`);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBid, setEditingBid] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [form, setForm] = useState({
    amount: '',
    note: '',
  });

  useEffect(() => {
    setIsLoading(bidsLoading);
  }, [bidsLoading]);

  if (!isAuthenticated() || user?.role !== 'waste_collector') {
    toast.error('Access denied. Waste collectors only.');
    return <Navigate to="/login" />;
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleEdit = (bid) => {
    setEditingBid(bid);
    setForm({
      amount: bid.bidAmount || '',
      note: bid.bidNote || '',
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (requestId, bidId) => {
    if (window.confirm('Are you sure you want to delete this bid?')) {
      try {
        await apiService.delete(`/waste/bid/${requestId}/${bidId}`);
        toast.success('Bid deleted successfully');
        fetchBids();
        setSelectedRequest(null); // Clear selected request on delete
      } catch (err) {
        toast.error(err.response?.data?.error || 'Failed to delete bid');
      }
    }
  };

  const handleUpdateStatus = async (requestId, bidId, status) => {
    try {
      await apiService.put(`/waste/bid/status/${requestId}/${bidId}`, { status });
      toast.success(`Bid marked as ${status}`);
      fetchBids();
    } catch (err) {
      toast.error(err.response?.data?.error || `Failed to update bid status to ${status}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiService.put(`/waste/bid/${editingBid.requestId}/${editingBid.bidId}`, {
        amount: Number(form.amount),
        note: form.note,
      });
      toast.success('Bid updated successfully');
      fetchBids();
      setIsModalOpen(false);
      setEditingBid(null);
      setForm({ amount: '', note: '' });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update bid');
    }
  };

  const renderAttachment = (attachmentUrl) => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
    const fullUrl = attachmentUrl.startsWith('http')
      ? attachmentUrl
      : `${baseUrl}/${attachmentUrl.replace(/^\/+/, '')}`;
    const ext = fullUrl.toLowerCase().split('.').pop();

    if (['jpg', 'jpeg', 'png'].includes(ext)) {
      return (
        <div className="relative group">
          <img
            src={fullUrl}
            alt="Attachment"
            className="w-32 h-32 object-cover rounded-lg border border-gray-300 dark:border-gray-600 transition-opacity duration-200 group-hover:opacity-90"
          />
          <button
            onClick={() => handleDownloadAttachment(attachmentUrl)}
            className="absolute top-2 right-2 bg-blue-600 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-blue-700"
            title="Download"
          >
            <FaDownload />
          </button>
        </div>
      );
    } else if (ext === 'pdf') {
      return (
        <div className="relative group">
          <iframe
            src={`${fullUrl}#toolbar=0&navpanes=0`}
            title="PDF Attachment"
            className="w-32 h-32 border rounded-lg bg-white dark:bg-gray-800 transition-opacity duration-200 group-hover:opacity-90"
            style={{ border: 'none' }}
          />
          <button
            onClick={() => handleDownloadAttachment(attachmentUrl)}
            className="absolute top-2 right-2 bg-blue-600 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-blue-700"
            title="Download"
          >
            <FaDownload />
          </button>
        </div>
      );
    }
    return (
      <div className="flex items-center space-x-2">
        <span className="text-gray-600 dark:text-gray-400">{attachmentUrl.split('/').pop()}</span>
        <button
          onClick={() => handleDownloadAttachment(attachmentUrl)}
          className="text-blue-600 hover:text-blue-800 transition-colors"
          title="Download"
        >
          <FaDownload />
        </button>
      </div>
    );
  };

  const handleDownloadAttachment = async (attachmentUrl) => {
    try {
      const { blob, contentType } = await apiService.getBlob(attachmentUrl);
      const url = window.URL.createObjectURL(new Blob([blob], { type: contentType }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachmentUrl.split('/').pop());
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Download started');
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Failed to download attachment');
    }
  };

  // Filter requests based on search term
  const filteredBids = bidsData?.bids.filter((bid) =>
    bid.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
    bid.quantity.toString().includes(searchTerm) ||
    bid.estimatedValue.toString().includes(searchTerm)
  );

  return (
    <div className="p-4 bg-gray-100 dark:bg-gray-900 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left Side - Searchable List */}
          <div className="w-full lg:w-1/3 space-y-4">
            <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-4">My Waste Bids</h2>
            <div className="relative mb-4">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by type, quantity, or value..."
                className="w-full p-2 pl-10 pr-4 border border-gray-300 rounded-lg dark:bg-gray-700 dark:text-white dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
              />
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
            </div>
            {isLoading ? (
              <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md text-center text-gray-600 dark:text-gray-300">Loading...</div>
            ) : bidsError ? (
              <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md text-center text-red-600 dark:text-red-400">Error: {bidsError}</div>
            ) : !filteredBids || filteredBids.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md text-center text-gray-600 dark:text-gray-300">No bids found.</div>
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
                {filteredBids.map((bid) => (
                  <div key={bid.requestId} className="border-b border-gray-200 dark:border-gray-700 last:border-b-0">
                    <div
                      className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center justify-between"
                      onClick={() => setSelectedRequest(bid)}
                    >
                      <div>
                        <h3 className="text-md font-medium text-gray-800 dark:text-gray-200">Request #{bid.requestId}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Type: {bid.type}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Quantity: {bid.quantity} kg</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Value: ${bid.estimatedValue}</p>
                      </div>
                      <span
                        className={`w-2 h-2 rounded-full ${selectedRequest?.requestId === bid.requestId ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right Side - Detailed View */}
          <div className="w-full lg:w-2/3">
            {selectedRequest ? (
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md space-y-6">
                <h3 className="text-2xl font-semibold text-gray-800 dark:text-gray-200">Request Details #{selectedRequest.requestId}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-700 dark:text-gray-300">
                  <div>
                    <p><strong>Farmer:</strong> {selectedRequest.farmer}</p>
                    <p><strong>Location:</strong> {selectedRequest.location?.city || 'N/A'}, {selectedRequest.location?.state || 'N/A'}, {selectedRequest.location?.country || 'N/A'} ({selectedRequest.location?.postalCode || 'N/A'})</p>
                    <p><strong>Deadline:</strong> {selectedRequest.deadline}</p>
                    <p><strong>Status:</strong> {selectedRequest.requestStatus}</p>
                    <p><strong>Priority:</strong> {selectedRequest.priority}</p>
                    <p><strong>Description:</strong> {selectedRequest.description || 'N/A'}</p>
                    <p><strong>Estimated Value:</strong> ${selectedRequest.estimatedValue}</p>
                    <p><strong>Processing Requirements:</strong> {selectedRequest.processingRequirements}</p>
                  </div>
                  <div>
                    <p><strong>Quantity:</strong> {selectedRequest.quantity} kg</p>
                    <p><strong>Type:</strong> {selectedRequest.type}</p>
                    <p><strong>Coordinates:</strong> [{selectedRequest.location?.coordinates?.join(', ') || 'N/A'}]</p>
                    <p><strong>Address:</strong> {selectedRequest.location?.address || 'N/A'}</p>
                  </div>
                </div>
                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2">My Bid Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-700 dark:text-gray-300">
                    <div>
                      <p><strong>Bid Amount:</strong> ${selectedRequest.bidAmount}</p>
                      <p><strong>Note:</strong> {selectedRequest.bidNote || 'N/A'}</p>
                      <p><strong>Bid Status:</strong> {selectedRequest.bidStatus}</p>
                      <p><strong>Created At:</strong> {selectedRequest.bidCreatedAt}</p>
                    </div>
                  </div>
                </div>
                {selectedRequest.attachments.length > 0 && (
                  <div>
                    <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2">Attachments</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {selectedRequest.attachments.map((attachment, index) => (
                        <div key={index} className="relative">
                          {renderAttachment(attachment)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex flex-wrap gap-4">
                  {selectedRequest.bidStatus === 'pending' && (
                    <>
                      <button
                        onClick={() => handleEdit(selectedRequest)}
                        className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors flex items-center"
                      >
                        <FaEdit className="mr-2" /> Edit
                      </button>
                      <button
                        onClick={() => handleDelete(selectedRequest.requestId, selectedRequest.bidId)}
                        className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center"
                      >
                        <FaTrash className="mr-2" /> Delete
                      </button>
                    </>
                  )}
                  {/* {selectedRequest.bidStatus === 'accepted' && (
                    <>
                      <button
                        onClick={() => handleUpdateStatus(selectedRequest.requestId, selectedRequest.bidId, 'completed')}
                        className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center"
                      >
                        <FaCheck className="mr-2" /> Mark Completed
                      </button>
                      <button
                        onClick={() => handleUpdateStatus(selectedRequest.requestId, selectedRequest.bidId, 'cancel')}
                        className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center"
                      >
                        <FaTimes className="mr-2" /> Cancel
                      </button>
                    </>
                  )} */}
                </div>
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md text-center text-gray-600 dark:text-gray-300">
                Select a request to view details.
              </div>
            )}
          </div>
        </div>
        <Modal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setEditingBid(null);
            setForm({ amount: '', note: '' });
          }}
          title="Edit Bid"
          className="max-w-md mx-auto"
        >
          <div className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <FormInput
                  label="Amount ($)"
                  name="amount"
                  type="number"
                  value={form.amount}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter bid amount"
                  className="w-full p-3 border border-gray-300 rounded-lg dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <FormInput
                  label="Note"
                  name="note"
                  type="text"
                  value={form.note}
                  onChange={handleInputChange}
                  placeholder="Enter note"
                  className="w-full p-3 border border-gray-300 rounded-lg dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false);
                    setEditingBid(null);
                    setForm({ amount: '', note: '' });
                  }}
                  className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  disabled={isLoading}
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        </Modal>
      </div>
    </div>
  );
}

export default MyWasteBids;